import {Dropdown} from "primereact/dropdown";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

import {useCheckPermission} from "../hooks/permissionHooks";
import type {AssignableUser} from "../redux/persistenceSlice";
import {loadFinalizeAssignableUsers} from "../redux/persistenceThunk";
import {useRevaDispatch} from "../redux/revaHooks";
import {PERMISSION_RULE_OWNERSHIP_ASSIGN} from "../util/permissions";

/** Dropdown-Sentinel für "Auftragspool" (Owner = null). */
const POOL_VALUE = "__pool__";

interface CorrectionOwnerSelectProps {
    /** Bewertung, die korrigiert/wieder geöffnet wird (für das Laden der finalisierungs-berechtigten User). */
    revaId: string | null | undefined;
    /** Aktuell gewählter neuer Owner-Username, oder null für den Auftragspool. */
    value: string | null;
    /**
     * Callback bei Auswahl. `owner` = gewählter Username bzw. null für den Auftragspool; `ownerLabel` =
     * das zugehörige Dropdown-Label (Anzeige-Name der Person bzw. das Pool-Label), z.B. für Meldungen.
     */
    onChange: (owner: string | null, ownerLabel: string) => void;
    /** Steuerung von außen sperren (z.B. während der Korrektur-Request läuft). */
    disabled?: boolean;
}

/**
 * Owner-Auswahl für die Korrektur-Dialoge: Nach dem Wieder-Öffnen einer finalisierten Bewertung zur Korrektur
 * wird ihr neuer Owner gewählt — entweder der Auftragspool (Owner = null, Standard) oder, NUR mit
 * reva-ownership-assign, eine konkrete Person, die die korrigierte Bewertung anschließend finalisieren darf.
 * Die Personen-Liste liefert {@link loadFinalizeAssignableUsers} (finalisierungs-berechtigte User, Snapshot-
 * Autoren dieser Bewertung zuerst). Ohne reva-ownership-assign steht nur der Pool zur Verfügung (und die Liste
 * wird gar nicht geladen — der Backend-Endpoint verlangt das Recht ohnehin).
 */
export function CorrectionOwnerSelect({revaId, value, onChange, disabled}: CorrectionOwnerSelectProps) {
    const {t} = useTranslation();
    const dispatch = useRevaDispatch();
    const canAssignToPerson = useCheckPermission(PERMISSION_RULE_OWNERSHIP_ASSIGN);
    const [users, setUsers] = useState<AssignableUser[] | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!canAssignToPerson || !revaId) {
            setUsers(null);
            return;
        }
        let active = true;
        setLoading(true);
        void dispatch(loadFinalizeAssignableUsers(revaId))
            .then((list) => {
                if (active) setUsers(list);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [dispatch, revaId, canAssignToPerson]);

    // Pool immer an erster Stelle; darunter (nur mit Recht) die finalisierungs-berechtigten User in der vom
    // Backend gelieferten Reihenfolge (Snapshot-Autoren dieser Bewertung zuerst).
    const options = [
        {label: t("value.correction.owner.pool"), value: POOL_VALUE},
        ...(users ?? []).map((user) => ({label: user.fullName ?? user.username, value: user.username})),
    ];

    return (
        <div className="mt-4">
            <label htmlFor="correction-owner" className="block mb-2">
                {t("value.correction.owner.label")}
            </label>
            <Dropdown
                inputId="correction-owner"
                className="w-full"
                value={value ?? POOL_VALUE}
                options={options}
                loading={loading}
                disabled={disabled || !canAssignToPerson || loading}
                onChange={(e) => {
                    const label = options.find((o) => o.value === e.value)?.label ?? "";
                    onChange(e.value === POOL_VALUE ? null : e.value, label);
                }}
            />
            {!canAssignToPerson && (
                <small className="block mt-1 text-surface-500 dark:text-surface-400">
                    {t("value.correction.owner.poolOnlyHint")}
                </small>
            )}
        </div>
    );
}
